import {
  BottomNavigation,
  BottomNavigationAction,
  Box,
  Button,
  Chip,
  GlobalStyles,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material';
import { useQuery } from 'convex/react';
import { motion } from 'framer-motion';
import {
  Album,
  Archive,
  Coffee,
  Disc3,
  ExternalLink,
  Gift,
  Headphones,
  Heart,
  Info,
  Moon,
  Shuffle,
  SlidersHorizontal,
  Sparkles,
  Zap,
  type LucideIcon,
} from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { isMobile } from 'react-device-detect';
import { Link as RouterLink } from 'react-router-dom';
import Tilty from 'react-tilty';
import { api } from '../../../../convex/_generated/api';
import { PlaylistTrack, usePlaylist } from '../../../contexts/PlaylistContext';
import useAppleMusicEnrichment from '../../../hooks/useAppleMusicEnrichment';
import useWindowSize from '../../../hooks/useWindowSize';
import { API_CACHE_TTL, fetchCachedGetApi } from '../../../utils/apiCache';
import {
  fetchAppleMusicEnrichment,
  getAppleMusicEnrichmentKey,
} from '../../../utils/appleMusicEnrichment';
import {
  buildFanTracks,
  FanPreferences,
  FanTrack,
  getAnniversaryReleases,
  getDailyPick,
  getDeepCut,
  getDynamicFanMoods,
  getFanStorageKey,
  getMoodScorePoints,
  getMoodPlaylistTracks,
  getMoodTracks,
  getPassportStats,
  getReleaseMixStats,
  getTimeline,
  getTimelineStats,
  loadFanPreferences,
  MoodId,
  saveFanPreferences,
  SpotifyAlbumSummary,
  TimelineStat,
} from '../../../utils/fanFeatures';
import { COMPANION_ARTIST_SLUG } from '../../../utils/companionPlaylists';
import {
  BREAKPOINT,
  formatDate,
  getArtistPath,
  getOptimizedImageUrl,
  safeStringWithHyphens,
  trackClick,
} from '../../_helpers';
import BaseModal from '../../ui/BaseModal';

interface FanHubProps {
  artistData: {
    name: string;
    images?: Array<{ url: string }>;
  };
  albums?: SpotifyAlbumSummary[];
}

type FanTab = 'today' | 'moods' | 'timeline' | 'passport';
type TimelineView = 'cards' | 'curve';

const panelSx = {
  backgroundColor: 'var(--color5)',
  color: 'var(--logoColor)',
  border: '1px solid rgba(255,255,255,0.16)',
  boxShadow: '0 1.25rem 4rem rgba(0,0,0,0.42)',
};

const fanHubControlChrome = {
  backgroundColor: 'color-mix(in srgb, var(--color5) 72%, transparent)',
  border: '1px solid color-mix(in srgb, var(--logoColor) 24%, transparent)',
  boxShadow: '0 0.75rem 2.25rem rgba(0,0,0,0.12), inset 0 1px 0 rgba(255,255,255,0.06)',
  backdropFilter: 'blur(18px) saturate(1.18)',
  WebkitBackdropFilter: 'blur(18px) saturate(1.18)',
};

const iconStyle = { width: '1em', height: '1em', strokeWidth: 2.2 };

const bottomNavIconStyle = { width: '20px', height: '20px', strokeWidth: 2.25 };

const fanHubDrawerBottomOffset = 'calc(8px + env(safe-area-inset-bottom, 0px))';
const fanHubDrawerMaxHeight = 'calc(100dvh - 18px - env(safe-area-inset-bottom, 0px))';

const moodChipSx = {
  height: { xs: '36px', md: '2rem' },
  borderRadius: '999px',
  fontFamily: 'Poppins',
  fontWeight: 800,
  fontSize: { xs: '13px', md: '0.8rem' },
  flexShrink: 0,
  '.MuiChip-label': {
    px: { xs: '13px', md: '0.7rem' },
  },
};

const computedMoodIconMap: Record<string, string> = {
  'high-energy': 'zap',
  'late-night': 'moon',
  focus: 'sunrise',
  worship: 'sparkles',
  christmas: 'sparkles',
  instrumentals: 'archive',
  'new-drops': 'zap',
};

const moodIconMap: Record<string, LucideIcon> = {
  sunrise: Coffee,
  zap: Zap,
  sparkles: Sparkles,
  moon: Moon,
  archive: Archive,
};

function MoodChipIcon({ icon }: { icon: string }) {
  const Icon = moodIconMap[icon] || Sparkles;
  return <Icon style={{ width: '15px', height: '15px', strokeWidth: 2.4 }} />;
}

function releasePath(artistName: string, track: FanTrack) {
  return getArtistPath(
    artistName,
    `/${track.releaseType === 'single' ? 'songs' : 'albums'}/${safeStringWithHyphens(track.releaseType === 'single' ? track.name : track.albumName)}`
  );
}

function toPlaylistTrack(track: FanTrack): PlaylistTrack | null {
  if (!track.previewUrl) return null;
  return {
    id: track.id,
    title: track.name,
    name: track.name,
    artist: track.artistName,
    artwork: track.albumImage,
    audioUrl: track.previewUrl,
    spotifyUrl: track.spotifyUrl,
    albumName: track.albumName,
  };
}

const formatPercent = (value: number) => `${Math.round(value * 100)}%`;

const surfaceSx = {
  borderRadius: '8px',
  backgroundColor: 'color-mix(in srgb, var(--color5) 86%, var(--color1))',
  border: '1px solid rgba(255,255,255,0.1)',
  boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.08)',
};

const helperLabelSx = {
  color: 'var(--logoColor)',
  fontFamily: 'Poppins',
  fontWeight: 800,
  fontSize: { xs: '11px', md: '0.72rem' },
  lineHeight: 1.1,
  opacity: 0.72,
  textTransform: 'uppercase',
};

const compactMetaSx = {
  color: 'var(--logoColor2)',
  fontFamily: 'Poppins',
  fontWeight: 700,
  fontSize: { xs: '12px', md: '0.78rem' },
  lineHeight: 1.2,
  opacity: 0.76,
};

function InfoButton({ label }: { label: string }) {
  return (
    <Tooltip title={label} arrow enterTouchDelay={0} leaveTouchDelay={3500}>
      <IconButton
        aria-label={label}
        sx={{
          color: 'var(--logoColor2)',
          p: { xs: '2px', md: '0.18rem' },
          opacity: 0.82,
          '& svg': { width: { xs: '13px', md: '0.9rem' }, height: { xs: '13px', md: '0.9rem' } },
        }}
      >
        <Info style={iconStyle} />
      </IconButton>
    </Tooltip>
  );
}

function SectionTitle({
  label,
  eyebrow,
  info,
}: {
  label: string;
  eyebrow?: string;
  info?: string;
}) {
  return (
    <Box>
      {eyebrow && <Typography sx={{ ...helperLabelSx, opacity: 0.64 }}>{eyebrow}</Typography>}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '5px', minWidth: 0 }}>
        <Typography
          sx={{
            color: 'var(--logoColor2)',
            fontFamily: 'Hanson',
            fontSize: { xs: '22px', md: '1rem' },
            lineHeight: 1.05,
            minWidth: 0,
          }}
        >
          {label}
        </Typography>
        {info && <InfoButton label={info} />}
      </Box>
    </Box>
  );
}

function MetricBar({
  label,
  value,
  detail,
  info,
}: {
  label: string;
  value: number;
  detail?: string;
  info?: string;
}) {
  return (
    <Box sx={{ display: 'grid', gap: { xs: '6px', md: '0.35rem' } }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: '0.75rem' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '3px', minWidth: 0 }}>
          <Typography
            sx={{
              ...helperLabelSx,
              fontSize: { xs: '12px', md: '0.75rem' },
              minWidth: 0,
              textTransform: 'none',
              opacity: 0.8,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {label}
          </Typography>
          {info && <InfoButton label={info} />}
        </Box>
        <Typography
          sx={{
            color: 'var(--logoColor2)',
            fontFamily: 'Poppins',
            fontWeight: 800,
            fontSize: { xs: '13px', md: '0.75rem' },
            lineHeight: 1.15,
            whiteSpace: 'nowrap',
          }}
        >
          {detail || formatPercent(value)}
        </Typography>
      </Box>
      <Box
        sx={{
          height: { xs: '7px', md: '0.55rem' },
          borderRadius: '999px',
          backgroundColor: 'rgba(1,1,1,0.24)',
          overflow: 'hidden',
        }}
      >
        <Box
          component={motion.div}
          initial={{ scaleX: 0 }}
          animate={{ scaleX: Math.max(0.04, Math.min(1, value)) }}
          transition={{ type: 'spring', stiffness: 95, damping: 18 }}
          sx={{
            width: '100%',
            height: '100%',
            transformOrigin: 'left',
            borderRadius: 'inherit',
            backgroundColor: 'var(--logoColor)',
          }}
        />
      </Box>
    </Box>
  );
}

function PassportRing({ value, label }: { value: number; label: string }) {
  const radius = 38;
  const circumference = 2 * Math.PI * radius;
  const clamped = Math.max(0, Math.min(1, value));
  return (
    <Box sx={{ display: 'grid', placeItems: 'center', gap: '0.45rem' }}>
      <Box
        component="svg"
        viewBox="0 0 96 96"
        sx={{ width: { xs: '68px', md: '5.25rem' }, height: { xs: '68px', md: '5.25rem' } }}
      >
        <circle cx="48" cy="48" r={radius} fill="none" stroke="rgba(1,1,1,0.25)" strokeWidth="9" />
        <motion.circle
          cx="48"
          cy="48"
          r={radius}
          fill="none"
          stroke="var(--logoColor)"
          strokeWidth="9"
          strokeLinecap="round"
          strokeDasharray={circumference}
          initial={{ strokeDashoffset: circumference }}
          animate={{ strokeDashoffset: circumference * (1 - clamped) }}
          transition={{ type: 'spring', stiffness: 80, damping: 20 }}
          transform="rotate(-90 48 48)"
        />
        <text
          x="48"
          y="53"
          textAnchor="middle"
          fill="var(--logoColor2)"
          fontFamily="Poppins"
          fontWeight="800"
          fontSize="16"
        >
          {formatPercent(clamped)}
        </text>
      </Box>
      <Typography
        sx={{
          ...helperLabelSx,
          fontSize: { xs: '12px', md: '0.7rem' },
          textAlign: 'center',
          textTransform: 'none',
        }}
      >
        {label}
      </Typography>
    </Box>
  );
}

function TimelineCurve({ stats }: { stats: TimelineStat[] }) {
  const maxCount = Math.max(1, ...stats.map((stat) => stat.count));

  return (
    <Box sx={{ p: { xs: '12px', md: '1.1rem' }, ...surfaceSx, overflow: 'hidden' }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'end',
          gap: { xs: '8px', md: '0.75rem' },
          height: { xs: '138px', md: '10rem' },
        }}
      >
        {stats.slice(0, 8).map((stat, index) => (
          <Box
            key={stat.year}
            sx={{
              flex: '1 1 0',
              minWidth: 0,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '7px',
              height: '100%',
              justifyContent: 'flex-end',
            }}
          >
            <Box
              component={motion.div}
              initial={{ scaleY: 0.3, opacity: 0.45 }}
              animate={{ scaleY: 1, opacity: 1 }}
              transition={{ delay: index * 0.04, type: 'spring', stiffness: 120, damping: 18 }}
              sx={{
                width: '100%',
                maxWidth: { xs: '38px', md: '3rem' },
                height: `${Math.max(18, (stat.count / maxCount) * 100)}%`,
                transformOrigin: 'bottom',
                borderRadius: '999px 999px 6px 6px',
                background: index % 2 === 0 ? 'var(--logoColor2)' : 'var(--logoColor)',
                boxShadow: '0 0.75rem 1.5rem rgba(1,1,1,0.18)',
              }}
            />
            <Typography
              sx={{
                color: 'var(--logoColor)',
                fontFamily: 'Poppins',
                fontWeight: 800,
                fontSize: { xs: '12px', md: '0.75rem' },
                lineHeight: 1,
              }}
            >
              {stat.year}
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
}

function TrackRow({
  track,
  artistName,
  favorite,
  onFavorite,
  compact = false,
}: {
  track: FanTrack;
  artistName: string;
  favorite: boolean;
  onFavorite: () => void;
  compact?: boolean;
}) {
  return (
    <Box
      data-apple-music-url={track.appleMusicUrl || undefined}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: { xs: compact ? '0.75rem' : '1rem', md: '0.85rem' },
        p: { xs: compact ? '0.62rem' : '0.9rem', md: '0.65rem' },
        borderRadius: '8px',
        backgroundColor: 'rgba(1,1,1,0.16)',
      }}
    >
      {track.albumImage && (
        <Box
          component="img"
          src={getOptimizedImageUrl(track.albumImage, {
            width: 120,
            height: 120,
            fit: 'cover',
            quality: 80,
          })}
          alt={track.name}
          sx={{
            width: { xs: compact ? '3.9rem' : '5rem', md: '3.25rem' },
            height: { xs: compact ? '3.9rem' : '5rem', md: '3.25rem' },
            borderRadius: '6px',
            objectFit: 'cover',
            flexShrink: 0,
          }}
        />
      )}
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography
          sx={{
            color: 'var(--logoColor)',
            fontFamily: 'Poppins',
            fontWeight: 800,
            fontSize: { xs: compact ? '18px' : '18px', md: '1rem' },
            lineHeight: 1.12,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {track.name}
        </Typography>
        <Typography
          sx={{
            ...compactMetaSx,
            fontSize: { xs: compact ? '12px' : '13px', md: '0.78rem' },
            mt: { xs: compact ? '3px' : '4px', md: '0.15rem' },
          }}
        >
          {track.releaseType.toUpperCase()} {formatDate(track.releaseDate)}
        </Typography>
      </Box>
      <IconButton
        onClick={onFavorite}
        sx={{
          color: 'var(--logoColor2)',
          p: { xs: compact ? '5px' : '8px', md: '0.5rem' },
          '& svg': {
            width: { xs: compact ? '17px' : '20px', md: '1em' },
            height: { xs: compact ? '17px' : '20px', md: '1em' },
          },
        }}
        aria-label={favorite ? 'Remove favorite' : 'Favorite track'}
      >
        {favorite ? <Heart fill="currentColor" style={iconStyle} /> : <Heart style={iconStyle} />}
      </IconButton>
      <IconButton
        component={RouterLink}
        to={releasePath(artistName, track)}
        sx={{
          color: 'var(--logoColor2)',
          p: { xs: compact ? '5px' : '8px', md: '0.5rem' },
          '& svg': {
            width: { xs: compact ? '17px' : '20px', md: '1em' },
            height: { xs: compact ? '17px' : '20px', md: '1em' },
          },
        }}
        aria-label="Open release"
      >
        <ExternalLink style={iconStyle} />
      </IconButton>
    </Box>
  );
}

function TodayCollectibleCard({
  track,
  artistName,
  favorite,
  onFavorite,
  recency,
  passport,
  fallbackArtwork,
}: {
  track: FanTrack;
  artistName: string;
  favorite: boolean;
  onFavorite: () => void;
  recency: number;
  passport: ReturnType<typeof getPassportStats>;
  fallbackArtwork?: string;
}) {
  const artwork = track.albumImage || fallbackArtwork;
  const card = (
    <Box
      component={motion.div}
      initial={{ opacity: 0, y: 14, scale: 0.98 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ type: 'spring', stiffness: 130, damping: 18 }}
      sx={{
        position: 'relative',
        overflow: 'hidden',
        borderRadius: { xs: '10px', md: '22px' },
        border: '1px solid color-mix(in srgb, var(--logoColor2) 34%, transparent)',
        backgroundColor: 'color-mix(in srgb, var(--color5) 84%, var(--color1))',
        boxShadow: '0 22px 48px rgba(0,0,0,0.36), inset 0 1px 0 rgba(255,255,255,0.1)',
        p: { xs: '12px', md: '1rem' },
        maxWidth: '100%',
      }}
    >
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '112px minmax(0, 1fr)', md: '13rem minmax(0, 1fr)' },
          gap: { xs: '12px', md: '1rem' },
          alignItems: 'stretch',
        }}
      >
        <Box
          sx={{
            position: 'relative',
            borderRadius: { xs: '7px', md: '16px' },
            overflow: 'hidden',
            aspectRatio: '1 / 1',
            backgroundColor: 'rgba(1,1,1,0.22)',
            boxShadow: '0 14px 28px rgba(0,0,0,0.32)',
            maxWidth: { xs: '110px', md: '100%' },
            height: { xs: '110px', md: '95%' },
            marginTop: '12px',
          }}
        >
          {artwork && (
            <Box
              component="img"
              src={getOptimizedImageUrl(artwork, {
                width: 480,
                height: 480,
                fit: 'cover',
                quality: 86,
              })}
              alt={track.name}
              sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
            />
          )}
          <Box
            sx={{
              position: 'absolute',
              left: '8px',
              bottom: '8px',
              px: '8px',
              py: '4px',
              borderRadius: '999px',
              backgroundColor: 'rgba(1,1,1,0.64)',
              color: 'var(--logoColor2)',
              fontFamily: 'Poppins',
              fontWeight: 900,
              fontSize: { xs: '10px', md: '0.7rem' },
              textTransform: 'uppercase',
              letterSpacing: '0.04em',
            }}
          >
            {track.releaseType}
          </Box>
        </Box>
        <Box sx={{ minWidth: 0, display: 'flex', flexDirection: 'column', gap: '10px' }}>
          <Box sx={{ minWidth: 0 }}>
            <Typography
              sx={{
                color: 'var(--logoColor2)',
                fontFamily: 'Hanson',
                fontSize: { xs: '10px', md: '0.75rem' },
                lineHeight: 1,
                opacity: 0.76,
                mt: '14px',
              }}
            >
              Today's Pick
            </Typography>
            <Typography
              sx={{
                color: 'var(--logoColor)',
                fontFamily: 'Hanson',
                fontSize: { xs: '21px', md: '1.45rem' },
                lineHeight: 1.05,
                mt: '6px',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {track.name}
            </Typography>
            <Typography
              sx={{
                color: 'var(--logoColor2)',
                fontFamily: 'Poppins',
                fontWeight: 800,
                fontSize: { xs: '13px', md: '0.82rem' },
                mt: '5px',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {track.albumName} - {formatDate(track.releaseDate)}
            </Typography>
          </Box>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
              gap: { xs: '6px', md: '0.55rem' },
            }}
          >
            {[
              ['Recency', formatPercent(recency)],
              ['Saved', String(passport.favoriteCount)],
              ['Visits', String(passport.visitCount)],
            ].map(([label, value]) => (
              <Box
                key={label}
                sx={{
                  borderRadius: '10px',
                  backgroundColor: 'rgba(1,1,1,0.18)',
                  p: { xs: '7px', md: '0.55rem' },
                  minWidth: 0,
                }}
              >
                <Typography
                  sx={{
                    color: 'var(--logoColor2)',
                    fontFamily: 'Poppins',
                    fontWeight: 900,
                    fontSize: { xs: '10px', md: '0.65rem' },
                    lineHeight: 1,
                    opacity: 0.8,
                  }}
                >
                  {label}
                </Typography>
                <Typography
                  sx={{
                    color: 'var(--logoColor)',
                    fontFamily: 'Hanson',
                    fontSize: { xs: '15px', md: '0.95rem' },
                    lineHeight: 1,
                    mt: '6px',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {value}
                </Typography>
              </Box>
            ))}
          </Box>
          <Box sx={{ display: 'flex', gap: '8px', mt: 'auto' }}>
            <Button
              component={RouterLink}
              to={releasePath(artistName, track)}
              startIcon={<Headphones style={iconStyle} />}
              sx={{
                flex: 1,
                minHeight: { xs: '42px', md: '2.65rem' },
                borderRadius: '999px',
                backgroundColor: 'var(--logoColor)',
                color: 'var(--color5)',
                fontFamily: 'Poppins',
                fontWeight: 900,
                fontSize: { xs: '13px', md: '0.85rem' },
                lineHeight: 1,
                textTransform: 'none',
                '& .MuiButton-startIcon': { mr: '6px' },
                '& svg': { width: { xs: '17px', md: '1em' }, height: { xs: '17px', md: '1em' } },
                '&:hover': { backgroundColor: 'var(--logoColor2)' },
              }}
            >
              Listen now
            </Button>
            <IconButton
              onClick={onFavorite}
              sx={{
                color: 'var(--logoColor2)',
                backgroundColor: 'rgba(255,255,255,0.06)',
                border: '1px solid rgba(255,255,255,0.12)',
                '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)' },
              }}
              aria-label={favorite ? 'Remove favorite' : 'Favorite track'}
            >
              {favorite ? (
                <Heart fill="currentColor" style={iconStyle} />
              ) : (
                <Heart style={iconStyle} />
              )}
            </IconButton>
          </Box>
        </Box>
      </Box>
    </Box>
  );

  if (typeof window === 'undefined') return card;

  return (
    <Tilty
      gyroscope
      perspective={900}
      max={12}
      reset={false}
      style={{ transformStyle: 'preserve-3d', transition: 'transform 0.25s ease' }}
    >
      {card}
    </Tilty>
  );
}

export function FanHub({ artistData, albums = [] }: FanHubProps) {
  const windowSize = useWindowSize();
  const reallyIsMobile = isMobile || windowSize.width <= BREAKPOINT;
  const artistName = artistData.name;
  const { addManyToPlaylist, isPanelOpen, togglePanel } = usePlaylist();
  const [open, setOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<FanTab>('today');
  const [selectedMood, setSelectedMood] = useState<MoodId>('focus');
  const [selectedComputedMood, setSelectedComputedMood] = useState<string | null>(null);
  const [albumDetails, setAlbumDetails] = useState<Record<string, any>>({});
  const [albumDetailsReadyKey, setAlbumDetailsReadyKey] = useState('');
  const [preferences, setPreferences] = useState<FanPreferences>(() =>
    loadFanPreferences(artistName)
  );
  const [shuffleSeed, setShuffleSeed] = useState(() => new Date().toISOString());
  const [moodActionStatus, setMoodActionStatus] = useState<string | null>(null);
  const [timelineView, setTimelineView] = useState<TimelineView>('cards');
  const [navTilt, setNavTilt] = useState({ x: 0, y: 0 });

  const sortedAlbums = useMemo(() => {
    return albums
      .filter((album) => album.album_group !== 'appears_on')
      .sort(
        (left, right) =>
          new Date(right.release_date).getTime() - new Date(left.release_date).getTime()
      );
  }, [albums]);
  const albumDetailsKey = useMemo(
    () => `${artistName}:${sortedAlbums.map((album) => album.id || album.name).join('|')}`,
    [artistName, sortedAlbums]
  );

  useEffect(() => {
    const next = loadFanPreferences(artistName);
    setPreferences(next);
    setSelectedMood(next.selectedMood || 'focus');
    setAlbumDetails({});
    setAlbumDetailsReadyKey('');
    saveFanPreferences(artistName, next);
  }, [artistName]);

  useEffect(() => {
    if (!reallyIsMobile || typeof window === 'undefined') return;

    const handleOrientation = (event: DeviceOrientationEvent) => {
      const gamma = Math.max(-18, Math.min(18, event.gamma || 0));
      const beta = Math.max(-18, Math.min(18, (event.beta || 0) - 45));
      setNavTilt({ x: gamma / 14, y: -beta / 18 });
    };

    window.addEventListener('deviceorientation', handleOrientation);
    return () => window.removeEventListener('deviceorientation', handleOrientation);
  }, [reallyIsMobile]);

  useEffect(() => {
    if (!open || sortedAlbums.length === 0) return;
    let cancelled = false;
    setAlbumDetailsReadyKey('');

    const loadDetails = async () => {
      const targetAlbums = sortedAlbums;
      const entries = await Promise.allSettled(
        targetAlbums.map((album) =>
          fetchCachedGetApi<{ album?: any }>(
            '/.netlify/functions/SpotifyGetArtistOneAlbum',
            { artist: artistName, album: album.name },
            {
              cacheKey: `fan-hub-album:${artistName.toLowerCase()}:${album.id || album.name}`,
              ttlMs: API_CACHE_TTL.SIX_HOURS,
              shouldCache: (data) => Boolean(data?.album),
            }
          ).then((data) => [album.id || album.name, data.album] as const)
        )
      );

      if (cancelled) return;
      setAlbumDetails((prev) => {
        const next = { ...prev };
        entries.forEach((entry) => {
          if (entry.status === 'fulfilled' && entry.value[1]) {
            next[entry.value[0]] = entry.value[1];
          }
        });
        return next;
      });
      setAlbumDetailsReadyKey(albumDetailsKey);
    };

    loadDetails();
    return () => {
      cancelled = true;
    };
  }, [albumDetailsKey, artistName, open, sortedAlbums]);

  const tracks = useMemo(
    () => buildFanTracks(artistName, sortedAlbums, albumDetails),
    [artistName, sortedAlbums, albumDetails]
  );
  const moodIdentityTracks = useMemo(
    () => buildFanTracks(artistName, sortedAlbums, {}),
    [artistName, sortedAlbums]
  );
  const dynamicMoods = useMemo(
    () => getDynamicFanMoods(moodIdentityTracks.length ? moodIdentityTracks : tracks, artistName),
    [artistName, moodIdentityTracks, tracks]
  );
  const moodCatalogReady = sortedAlbums.length === 0 || albumDetailsReadyKey === albumDetailsKey;
  const isCompanionArtist = safeStringWithHyphens(artistName || '') === COMPANION_ARTIST_SLUG;
  const computedMoods = useQuery(
    api.companionPlaylists.listMoodPlaylists,
    isCompanionArtist && open ? {} : 'skip'
  );
  const useComputedMoods = Boolean(computedMoods && computedMoods.length > 0);
  const activeComputedMoodId = useComputedMoods
    ? selectedComputedMood && computedMoods?.some((mood) => mood.moodId === selectedComputedMood)
      ? selectedComputedMood
      : computedMoods?.[0]?.moodId || null
    : null;
  const activeComputedMood = computedMoods?.find((mood) => mood.moodId === activeComputedMoodId) || null;
  const computedMoodDetail = useQuery(
    api.companionPlaylists.getMoodPlaylist,
    activeComputedMoodId ? { moodId: activeComputedMoodId } : 'skip'
  );
  const computedMoodTracks = useMemo(() => {
    if (!computedMoodDetail) return [];
    const byId = new Map(tracks.map((track) => [track.id, track]));
    return computedMoodDetail.tracks
      .map((entry) => byId.get(entry.spotifyTrackId))
      .filter(Boolean) as FanTrack[];
  }, [computedMoodDetail, tracks]);
  const dailyPick = useMemo(() => getDailyPick(tracks, artistName), [artistName, tracks]);
  const deepCut = useMemo(
    () => getDeepCut(tracks, artistName, shuffleSeed),
    [artistName, tracks, shuffleSeed]
  );
  const moodTracks = useMemo(
    () => getMoodTracks(tracks, selectedMood, 8, dynamicMoods),
    [dynamicMoods, tracks, selectedMood]
  );
  const moodScorePoints = useMemo(
    () => getMoodScorePoints(tracks, selectedMood, 6, dynamicMoods),
    [dynamicMoods, tracks, selectedMood]
  );
  const visibleMoodTracks = useMemo(() => {
    if (useComputedMoods) return computedMoodTracks.slice(0, 6);
    const chartTrackKeys = new Set(
      moodScorePoints.map((point) => point.track.name.toLowerCase().replace(/[^a-z0-9]/g, ''))
    );
    const remainder = moodTracks.filter(
      (track) => !chartTrackKeys.has(track.name.toLowerCase().replace(/[^a-z0-9]/g, ''))
    );
    return (remainder.length > 0 ? remainder : moodTracks).slice(0, 5);
  }, [computedMoodTracks, moodScorePoints, moodTracks, useComputedMoods]);
  const visibleAppleMusicQueries = useMemo(() => {
    const candidates = [dailyPick, deepCut, ...visibleMoodTracks].filter(Boolean) as FanTrack[];
    return candidates.map((track) => ({
      artistName,
      releaseName: track.releaseType === 'single' ? track.name : track.albumName,
      albumName: track.albumName,
      lookupType: (track.releaseType === 'single' ? 'song' : 'album') as 'song' | 'album',
      spotifyId: track.id,
    }));
  }, [artistName, dailyPick, deepCut, visibleMoodTracks]);
  const { enrichmentByKey: fanTrackAppleMusicByKey } = useAppleMusicEnrichment(
    visibleAppleMusicQueries,
    {
      enabled: open,
      ttlMs: API_CACHE_TTL.SIX_HOURS,
      concurrency: 2,
    }
  );
  const enrichFanTrack = (track: FanTrack): FanTrack => {
    const query = {
      artistName,
      releaseName: track.releaseType === 'single' ? track.name : track.albumName,
      albumName: track.albumName,
      lookupType: (track.releaseType === 'single' ? 'song' : 'album') as 'song' | 'album',
      spotifyId: track.id,
    };
    const enrichment = fanTrackAppleMusicByKey[getAppleMusicEnrichmentKey(query)];
    return {
      ...track,
      appleMusicUrl: enrichment?.url,
      albumImage: enrichment?.artworkUrl || track.albumImage,
      previewUrl: track.previewUrl || enrichment?.previewUrl,
    };
  };
  const timeline = useMemo(() => getTimeline(sortedAlbums), [sortedAlbums]);
  const timelineStats = useMemo(() => getTimelineStats(sortedAlbums), [sortedAlbums]);
  const releaseMixStats = useMemo(() => getReleaseMixStats(sortedAlbums), [sortedAlbums]);
  const anniversaries = useMemo(() => getAnniversaryReleases(sortedAlbums), [sortedAlbums]);
  const passport = useMemo(
    () => getPassportStats(tracks, preferences, dynamicMoods),
    [dynamicMoods, tracks, preferences]
  );
  const favoriteShare = tracks.length > 0 ? passport.favoriteCount / tracks.length : 0;
  const visitShare = Math.min(1, passport.visitCount / 12);
  const catalogShare =
    sortedAlbums.length > 0 ? Math.min(1, passport.releaseCount / sortedAlbums.length) : 0;
  const persist = (patch: Partial<FanPreferences>) => {
    setPreferences((prev) => {
      const next = { ...prev, ...patch, lastVisitAt: new Date().toISOString() };
      saveFanPreferences(artistName, next);
      return next;
    });
  };

  const toggleFavorite = (track: FanTrack) => {
    trackClick(preferences.favoriteTrackIds.includes(track.id) ? 'fanhub-unfavorite' : 'fanhub-favorite', {
      label: track.name,
      section: 'fan-hub',
      artist: artistName,
      track: track.name,
      trackId: track.id,
    });
    const favoriteTrackIds = preferences.favoriteTrackIds.includes(track.id)
      ? preferences.favoriteTrackIds.filter((id) => id !== track.id)
      : [...preferences.favoriteTrackIds, track.id];
    persist({ favoriteTrackIds });
  };

  const openFeature = (tab: FanTab) => {
    trackClick(`fanhub-bar:${tab}`, { label: `Fan hub ${tab}`, section: 'fan-hub-bar', artist: artistName });
    setActiveTab(tab);
    setOpen(true);
    persist({
      featureOpens: {
        ...preferences.featureOpens,
        [tab]: (preferences.featureOpens[tab] || 0) + 1,
      },
    });
  };

  const selectMood = (moodId: MoodId) => {
    trackClick(`fanhub-mood:${moodId}`, { label: `Mood ${moodId}`, section: 'fan-hub', artist: artistName });
    setSelectedMood(moodId);
    persist({ selectedMood: moodId });
  };

  const selectComputedMood = (moodId: string) => {
    trackClick(`fanhub-mood:${moodId}`, { label: `Mood ${moodId}`, section: 'fan-hub', artist: artistName });
    setSelectedComputedMood(moodId);
  };

  const activeMood = dynamicMoods.find((mood) => mood.id === selectedMood) || dynamicMoods[0];
  const listenToMood = async () => {
    const listenMoodId = useComputedMoods ? activeComputedMoodId : selectedMood;
    trackClick('fanhub-listen-mood', { label: `Listen to mood ${listenMoodId}`, section: 'fan-hub', artist: artistName });
    if (!moodCatalogReady) {
      setMoodActionStatus('Loading the full catalog for this mood...');
      return;
    }

    setMoodActionStatus('Loading previews...');
    const playlistMoodTracks = useComputedMoods
      ? computedMoodTracks
      : getMoodPlaylistTracks(tracks, selectedMood, { minTracks: 10, maxTracks: 20 }, dynamicMoods);
    const playableTracks = (
      await Promise.all(
        playlistMoodTracks.map(async (track) => {
          const enrichedTrack = enrichFanTrack(track);
          if (enrichedTrack.previewUrl) return toPlaylistTrack(enrichedTrack);

          try {
            const enrichment = await fetchAppleMusicEnrichment(
              {
                artistName,
                releaseName: track.name,
                albumName: track.albumName,
                lookupType: 'song',
                spotifyId: track.id,
              },
              {
                ttlMs: API_CACHE_TTL.SIX_HOURS,
              }
            );

            return toPlaylistTrack({
              ...enrichedTrack,
              previewUrl: enrichment?.previewUrl || enrichedTrack.previewUrl,
              albumImage: enrichment?.artworkUrl || enrichedTrack.albumImage,
            });
          } catch {
            return null;
          }
        })
      )
    ).filter(Boolean) as PlaylistTrack[];
    const queuedCount = addManyToPlaylist(playableTracks);

    if (playableTracks.length === 0) {
      setMoodActionStatus('No previews are available for these mood picks yet.');
      return;
    }

    setMoodActionStatus(
      `${queuedCount > 0 ? queuedCount : playableTracks.length} preview${queuedCount === 1 ? '' : 's'} ready in your playlist.`
    );
    setOpen(false);
    if (!isPanelOpen) togglePanel();
  };
  const fallbackArtwork =
    dailyPick?.albumImage || sortedAlbums[0]?.images?.[0]?.url || artistData.images?.[0]?.url;
  const dailyRecency = dailyPick
    ? Math.max(
        0.12,
        Math.min(
          1,
          1 - Math.abs(Date.now() - new Date(dailyPick.releaseDate).getTime()) / 126144000000
        )
      )
    : 0.12;
  const visualArtwork = fallbackArtwork;
  const moodSummary = useComputedMoods
    ? activeComputedMood
      ? `${activeComputedMood.description}${activeComputedMood.firstTrackNames.length > 0 ? ` Opens with ${activeComputedMood.firstTrackNames.join(', ')}.` : ''}`
      : `No mood playlists are ready for ${artistName} yet.`
    : !moodCatalogReady && activeMood
      ? `${activeMood.summary} Full-catalog picks are loading before this mood locks in.`
      : moodScorePoints.length && activeMood
      ? `${activeMood.summary} Top matches: ${moodScorePoints[0].track.name}${moodScorePoints[1] ? ` and ${moodScorePoints[1].track.name}` : ''}.`
      : `No catalog matches are loaded from ${artistName}'s catalog yet.`;
  const maxTimelineCount = Math.max(1, ...timelineStats.map((item) => item.count));

  const content = (
    <Box
      sx={{
        width: { xs: '100%', md: 'min(64rem, calc(100vw - 4rem))' },
        maxHeight: { xs: 'none', md: 'min(84vh, 56rem)' },
        overflow: { xs: 'hidden', md: 'auto' },
        boxSizing: 'border-box',
        p: { xs: '10px 10px 12px', md: '1.5rem' },
        ...panelSx,
        backgroundColor: 'var(--color5)',
      }}
      data-lenis-prevent
    >
      <Box
        sx={{
          position: 'relative',
          overflow: 'hidden',
          borderRadius: { xs: '10px', md: '8px' },
          minHeight: { xs: '92px', md: '7.5rem' },
          mb: { xs: '8px', md: '1rem' },
          p: { xs: '14px', md: '1.1rem' },
          display: 'flex',
          alignItems: 'flex-end',
          justifyContent: 'space-between',
          gap: '1rem',
          backgroundColor: 'color-mix(in srgb, var(--color1) 70%, var(--color5))',
          '&::before': visualArtwork
            ? {
                content: '""',
                position: 'absolute',
                inset: 0,
                backgroundImage: `url(${getOptimizedImageUrl(visualArtwork, { width: 720, quality: 72 })})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                opacity: 0.34,
                zIndex: 0,
              }
            : undefined,
          '&::after': {
            content: '""',
            position: 'absolute',
            inset: 0,
            backgroundColor: 'var(--color5)',
            opacity: 0.64,
            zIndex: 0,
          },
        }}
      >
        <Box sx={{ position: 'relative', zIndex: 1 }}>
          <Typography
            sx={{
              color: 'var(--logoColor2)',
              fontFamily: 'Hanson',
              fontSize: { xs: '25px', md: '1.125rem' },
              lineHeight: 1,
              textTransform: 'capitalize',
            }}
          >
            {activeTab === 'today'
              ? "Today's Pick"
              : activeTab === 'moods'
                ? 'Moods Playlist'
                : activeTab === 'timeline'
                  ? 'Timeline'
                  : ''}
          </Typography>
          <Typography
            sx={{
              color: 'var(--logoColor)',
              fontFamily: 'Poppins',
              fontSize: { xs: '12px', md: '0.5rem' },
              opacity: 0.82,
              mt: '8px',
            }}
          >
            {artistName}'s Fan Hub - saved on this device
          </Typography>
        </Box>
      </Box>

      {activeTab === 'today' && (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '1.05fr 0.95fr' },
            gap: { xs: '12px', md: '1rem' },
            maxWidth: '100%',
          }}
        >
          {dailyPick && (
            <TodayCollectibleCard
              track={enrichFanTrack(dailyPick)}
              artistName={artistName}
              favorite={preferences.favoriteTrackIds.includes(dailyPick.id)}
              onFavorite={() => toggleFavorite(dailyPick)}
              recency={dailyRecency}
              passport={passport}
              fallbackArtwork={fallbackArtwork}
            />
          )}
          <Box
            sx={{ display: 'flex', flexDirection: 'column', gap: { xs: '10px', md: '0.75rem' } }}
          >
            {deepCut && (
              <Box sx={{ p: { xs: '10px', md: '0.85rem' }, ...surfaceSx }}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: '0.75rem',
                    mb: '0.55rem',
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: '5px', minWidth: 0 }}>
                    <Shuffle style={{ ...iconStyle, color: 'var(--logoColor2)' }} />
                    <Typography sx={helperLabelSx}>Deep cut</Typography>
                    <InfoButton label="A less obvious catalog pick, weighted toward older or deeper tracks from real release data." />
                  </Box>
                  <Button
                    onClick={() => {
                      trackClick('fanhub-shuffle', { label: 'Shuffle fan hub picks', section: 'fan-hub', artist: artistName });
                      setShuffleSeed(new Date().toISOString());
                    }}
                    sx={{
                      color: 'var(--logoColor2)',
                      fontWeight: 800,
                      fontSize: { xs: '11px', md: '0.95rem' },
                      minWidth: 'auto',
                      px: { xs: '6px', md: '1rem' },
                    }}
                  >
                    Shuffle
                  </Button>
                </Box>
                <TrackRow
                  track={enrichFanTrack(deepCut)}
                  artistName={artistName}
                  favorite={preferences.favoriteTrackIds.includes(deepCut.id)}
                  onFavorite={() => toggleFavorite(deepCut)}
                  compact
                />
              </Box>
            )}
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
              <MetricBar
                label="Saved picks"
                info="Tracks you have favorited in this Fan Hub on this device."
                value={favoriteShare}
                detail={`${passport.favoriteCount}/${Math.max(1, tracks.length)}`}
              />
              <MetricBar
                label="Fan rhythm"
                info="How often this Fan Hub has been opened on this device."
                value={visitShare}
                detail={`${passport.visitCount} visits`}
              />
            </Box>
            <Button
              component={RouterLink}
              to={dailyPick ? releasePath(artistName, dailyPick) : getArtistPath(artistName)}
              startIcon={<Headphones style={iconStyle} />}
              sx={{
                backgroundColor: 'var(--logoColor)',
                color: 'var(--color5)',
                fontWeight: 800,
                fontSize: { xs: '13px', md: '0.95rem' },
                minHeight: { xs: '40px', md: 'auto' },
                '&:hover': { backgroundColor: 'var(--logoColor2)' },
              }}
            >
              Listen now
            </Button>
          </Box>
        </Box>
      )}

      {activeTab === 'moods' && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: '12px', md: '1rem' } }}>
          <Box
            sx={{
              display: 'flex',
              gap: { xs: '8px', md: '0.75rem' },
              flexWrap: 'nowrap',
              overflowX: 'auto',
              overflowY: 'hidden',
              pb: '2px',
              mx: { xs: '-2px', md: 0 },
              px: { xs: '2px', md: 0 },
            }}
          >
            {(useComputedMoods
              ? (computedMoods || []).map((mood) => ({
                  id: mood.moodId,
                  label: mood.name,
                  icon: computedMoodIconMap[mood.moodId] || 'sparkles',
                  selected: mood.moodId === activeComputedMoodId,
                  onSelect: () => selectComputedMood(mood.moodId),
                }))
              : dynamicMoods.map((mood) => ({
                  id: mood.id,
                  label: mood.label,
                  icon: mood.icon,
                  selected: mood.id === selectedMood,
                  onSelect: () => selectMood(mood.id),
                }))
            ).map((mood) => (
              <Chip
                key={mood.id}
                icon={<MoodChipIcon icon={mood.icon} />}
                label={mood.label}
                onClick={mood.onSelect}
                sx={{
                  ...moodChipSx,
                  pl: { xs: '9px', md: '0.5rem' },
                  pr: { xs: '3px', md: '0.2rem' },
                  backgroundColor: mood.selected ? 'var(--logoColor)' : 'rgba(1,1,1,0.18)',
                  color: mood.selected ? 'var(--color5)' : 'var(--logoColor)',
                  '& .MuiChip-icon': {
                    color: 'inherit',
                    ml: '0 !important',
                    mr: { xs: '5px !important', md: '0.35rem !important' },
                    flexShrink: 0,
                  },
                  '& .MuiChip-label': {
                    px: { xs: '6px', md: '0.35rem' },
                  },
                }}
              />
            ))}
          </Box>
          <Typography
            sx={{
              color: 'var(--logoColor)',
              fontFamily: 'Poppins',
              fontSize: { xs: '14px', md: '0.75rem' },
              lineHeight: 1.45,
              opacity: 0.86,
            }}
          >
            {moodSummary}
          </Typography>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: useComputedMoods ? '1fr' : '0.9fr 1.1fr' },
              gap: { xs: '12px', md: '1rem' },
            }}
          >
            {useComputedMoods ? null : (
            <Box sx={{ p: { xs: '12px', md: '1.1rem' }, ...surfaceSx }}>
              <SectionTitle
                label={`${activeMood.label} map`}
                eyebrow="Mood fit"
                info="Mood fit is scored from release names, track titles, dates, duration, and album depth from this artist's real catalog."
              />
              <Box
                sx={{
                  display: 'grid',
                  gap: { xs: '12px', md: '0.55rem' },
                  mt: { xs: '12px', md: '1rem' },
                }}
              >
                {moodCatalogReady ? (
                  moodScorePoints.map((point) => (
                    <MetricBar key={point.track.id} label={point.track.name} value={point.score} />
                  ))
                ) : (
                  [0, 1, 2, 3, 4].map((item) => (
                    <Box
                      key={item}
                      sx={{
                        height: { xs: '29px', md: '1.9rem' },
                        borderRadius: '999px',
                        backgroundColor: 'rgba(255,255,255,0.1)',
                        overflow: 'hidden',
                        position: 'relative',
                        animation: 'fan-hub-pulse 1.2s ease-in-out infinite alternate',
                        '@keyframes fan-hub-pulse': {
                          from: { opacity: 0.52 },
                          to: { opacity: 0.84 },
                        },
                      }}
                    />
                  ))
                )}
              </Box>
            </Box>
            )}
            <Box
              sx={{ display: 'flex', flexDirection: 'column', gap: { xs: '10px', md: '0.7rem' } }}
            >
              {moodCatalogReady
                ? visibleMoodTracks.map((track) => (
                    <TrackRow
                      key={track.id}
                      track={enrichFanTrack(track)}
                      artistName={artistName}
                      favorite={preferences.favoriteTrackIds.includes(track.id)}
                      onFavorite={() => toggleFavorite(track)}
                    />
                  ))
                : moodIdentityTracks.slice(0, 5).map((track) => (
                    <TrackRow
                      key={track.id}
                      track={track}
                      artistName={artistName}
                      favorite={false}
                      onFavorite={() => undefined}
                    />
                  ))}
            </Box>
          </Box>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: activeComputedMood ? { xs: '1fr', md: 'auto auto' } : 'auto',
              justifyContent: 'start',
              gap: { xs: '8px', md: '0.75rem' },
            }}
          >
            <Button
              onClick={listenToMood}
              startIcon={<Headphones style={iconStyle} />}
              sx={{
                color: 'var(--color5)',
                backgroundColor: 'var(--logoColor)',
                fontWeight: 800,
                fontSize: { xs: '13px', md: '1rem' },
                minHeight: { xs: '42px', md: 'auto' },
                px: { xs: '8px', md: '1rem' },
                lineHeight: 1.1,
                '& .MuiButton-startIcon': { mr: { xs: '4px', md: '8px' } },
                '& svg': { width: { xs: '18px', md: '1em' }, height: { xs: '18px', md: '1em' } },
                '&:hover': { backgroundColor: 'var(--logoColor2)' },
              }}
            >
              Listen now
            </Button>
            {activeComputedMood ? (
              <Button
                component="a"
                href={activeComputedMood.spotifyUrl}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() =>
                  trackClick(`mood-playlist:${activeComputedMood.moodId}`, {
                    label: activeComputedMood.name,
                    section: 'fan-hub',
                    artist: artistName,
                    href: activeComputedMood.spotifyUrl,
                  })
                }
                startIcon={<ExternalLink style={iconStyle} />}
                sx={{
                  color: 'var(--logoColor)',
                  border: '1px solid color-mix(in srgb, var(--logoColor) 34%, transparent)',
                  fontWeight: 800,
                  fontSize: { xs: '13px', md: '1rem' },
                  minHeight: { xs: '42px', md: 'auto' },
                  px: { xs: '8px', md: '1rem' },
                  lineHeight: 1.1,
                  '& .MuiButton-startIcon': { mr: { xs: '4px', md: '8px' } },
                  '& svg': { width: { xs: '18px', md: '1em' }, height: { xs: '18px', md: '1em' } },
                  '&:hover': { backgroundColor: 'rgba(255,255,255,0.06)' },
                }}
              >
                Open on Spotify
              </Button>
            ) : null}
          </Box>
          {moodActionStatus && (
            <Typography
              sx={{
                color: 'var(--logoColor)',
                fontFamily: 'Poppins',
                fontWeight: 700,
                fontSize: { xs: '13px', md: '0.78rem' },
                opacity: 0.76,
              }}
            >
              {moodActionStatus}
            </Typography>
          )}
        </Box>
      )}

      {activeTab === 'timeline' && (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
            gap: { xs: '12px', md: '1rem' },
          }}
        >
          <Box
            sx={{ display: 'flex', flexDirection: 'column', gap: { xs: '12px', md: '0.75rem' } }}
          >
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: '0.75rem',
              }}
            >
              <SectionTitle
                label="Timeline"
                eyebrow="Era chart"
                info="Release eras are grouped by year from this artist's real Spotify catalog."
              />
              <Box sx={{ display: 'flex', gap: '5px', flexShrink: 0 }}>
                {(['cards', 'curve'] as TimelineView[]).map((view) => (
                  <Button
                    key={view}
                    onClick={() => {
                      trackClick(`fanhub-timeline:${view}`, { label: `Timeline ${view}`, section: 'fan-hub', artist: artistName });
                      setTimelineView(view);
                    }}
                    sx={{
                      minWidth: 0,
                      px: { xs: '9px', md: '0.8rem' },
                      py: { xs: '5px', md: '0.35rem' },
                      borderRadius: '999px',
                      color: timelineView === view ? 'var(--color5)' : 'var(--logoColor)',
                      backgroundColor:
                        timelineView === view ? 'var(--logoColor)' : 'rgba(1,1,1,0.18)',
                      fontFamily: 'Poppins',
                      fontWeight: 800,
                      fontSize: { xs: '11px', md: '0.72rem' },
                      '&:hover': {
                        backgroundColor:
                          timelineView === view ? 'var(--logoColor2)' : 'rgba(1,1,1,0.24)',
                      },
                    }}
                  >
                    {view}
                  </Button>
                ))}
              </Box>
            </Box>
            {timelineView === 'curve' ? (
              <TimelineCurve stats={timelineStats} />
            ) : (
              <Box
                sx={{
                  p: { xs: '12px', md: '1.1rem' },
                  ...surfaceSx,
                  display: 'grid',
                  gap: { xs: '10px', md: '0.7rem' },
                }}
              >
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
                    gap: { xs: '10px', md: '0.7rem' },
                  }}
                >
                  {timelineStats.slice(0, 6).map((stat) => (
                    <Box
                      key={stat.year}
                      sx={{
                        borderRadius: '8px',
                        p: { xs: '10px', md: '0.75rem' },
                        backgroundColor:
                          stat.count === maxTimelineCount ? 'var(--logoColor)' : 'rgba(1,1,1,0.18)',
                        color:
                          stat.count === maxTimelineCount ? 'var(--color5)' : 'var(--logoColor)',
                      }}
                    >
                      <Typography
                        sx={{
                          fontFamily: 'Hanson',
                          fontSize: { xs: '20px', md: '1rem' },
                          color: 'inherit',
                          lineHeight: 1,
                        }}
                      >
                        {stat.year}
                      </Typography>
                      <Typography
                        sx={{
                          fontFamily: 'Poppins',
                          fontWeight: 800,
                          fontSize: { xs: '15px', md: '0.78rem' },
                          color: 'inherit',
                          mt: '6px',
                        }}
                      >
                        {stat.count} release{stat.count === 1 ? '' : 's'}
                      </Typography>
                      <Box
                        sx={{
                          height: '6px',
                          borderRadius: '999px',
                          backgroundColor: 'rgba(1,1,1,0.2)',
                          mt: '8px',
                          overflow: 'hidden',
                        }}
                      >
                        <Box
                          sx={{
                            width: `${Math.max(8, stat.share * 100)}%`,
                            height: '100%',
                            borderRadius: 'inherit',
                            backgroundColor:
                              stat.count === maxTimelineCount
                                ? 'var(--color5)'
                                : 'var(--logoColor2)',
                          }}
                        />
                      </Box>
                    </Box>
                  ))}
                </Box>
              </Box>
            )}
            {timeline.slice(0, 6).map((group) => (
              <Box
                key={group.year}
                sx={{
                  p: { xs: '12px', md: '0.85rem' },
                  ...surfaceSx,
                  display: 'grid',
                  gridTemplateColumns: 'auto minmax(0, 1fr)',
                  alignItems: 'center',
                  gap: { xs: '12px', md: '0.75rem' },
                  minWidth: 0,
                }}
              >
                <Typography
                  sx={{
                    color: 'var(--logoColor2)',
                    fontFamily: 'Hanson',
                    fontSize: { xs: '20px', md: '0.95rem' },
                    minWidth: { xs: '58px', md: 'auto' },
                  }}
                >
                  {group.year}
                </Typography>
                <Typography
                  sx={{
                    color: 'var(--logoColor)',
                    fontFamily: 'Poppins',
                    fontWeight: 800,
                    fontSize: { xs: '16px', md: '0.78rem' },
                    lineHeight: 1.25,
                    minWidth: 0,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {group.releases.length} release{group.releases.length === 1 ? '' : 's'} ·{' '}
                  {group.releases[0]?.name}
                </Typography>
              </Box>
            ))}
          </Box>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: { xs: '12px', md: '0.75rem' },
              mt: 3,
            }}
          >
            <SectionTitle
              label="Anniversary mode"
              eyebrow="Return dates"
              info="Upcoming or recent release anniversaries from this artist's catalog."
            />
            {anniversaries.length > 0 ? (
              anniversaries.slice(0, 5).map(({ album, daysUntil, years }) => (
                <Box
                  key={album.id || album.name}
                  sx={{
                    p: { xs: '12px', md: '0.85rem' },
                    ...surfaceSx,
                    display: 'flex',
                    alignItems: 'center',
                    gap: { xs: '12px', md: '0.75rem' },
                  }}
                >
                  {album.images?.[0]?.url ? (
                    <Box
                      component="img"
                      src={getOptimizedImageUrl(album.images[0].url, {
                        width: 96,
                        height: 96,
                        fit: 'cover',
                        quality: 80,
                      })}
                      alt={album.name}
                      sx={{
                        width: { xs: '44px', md: '2.75rem' },
                        height: { xs: '44px', md: '2.75rem' },
                        borderRadius: '6px',
                        objectFit: 'cover',
                        flexShrink: 0,
                      }}
                    />
                  ) : (
                    <Gift
                      style={{
                        width: reallyIsMobile ? '22px' : '1em',
                        height: reallyIsMobile ? '22px' : '1em',
                        color: 'var(--logoColor2)',
                        flexShrink: 0,
                      }}
                    />
                  )}
                  <Box sx={{ minWidth: 0 }}>
                    <Typography
                      sx={{
                        color: 'var(--logoColor)',
                        fontFamily: 'Poppins',
                        fontWeight: 800,
                        fontSize: { xs: '17px', md: '1rem' },
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {album.name}
                    </Typography>
                    <Typography
                      sx={{
                        color: 'var(--logoColor2)',
                        fontFamily: 'Poppins',
                        fontWeight: 700,
                        fontSize: { xs: '14px', md: '0.78rem' },
                      }}
                    >
                      {daysUntil === 0
                        ? 'Today'
                        : daysUntil > 0
                          ? `In ${daysUntil} days`
                          : `${Math.abs(daysUntil)} days ago`}{' '}
                      - {years} year{years === 1 ? '' : 's'}
                    </Typography>
                  </Box>
                </Box>
              ))
            ) : (
              <Typography sx={{ color: 'var(--logoColor)', fontFamily: 'Poppins', opacity: 0.75 }}>
                No release anniversaries in the next month.
              </Typography>
            )}
          </Box>
        </Box>
      )}

      {activeTab === 'passport' && (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '0.9fr 1.1fr' },
            gap: '1rem',
          }}
        >
          <Box sx={{ p: { xs: '1rem', md: '1.1rem' }, ...surfaceSx }}>
            <SectionTitle
              label="Fan passport"
              eyebrow="Saved locally"
              info="A local-only profile of what this device has saved, opened, and explored."
            />
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(3, 1fr)',
                gap: '0.65rem',
                mt: '1rem',
              }}
            >
              <PassportRing value={favoriteShare} label="Saved" />
              <PassportRing value={visitShare} label="Visits" />
              <PassportRing value={catalogShare} label="Catalog" />
            </Box>
            <Box sx={{ display: 'grid', gap: '0.65rem', mt: '1rem' }}>
              {releaseMixStats.map((stat) => (
                <MetricBar
                  key={stat.type}
                  label={stat.type.toUpperCase()}
                  value={stat.share}
                  detail={`${stat.count}`}
                />
              ))}
            </Box>
          </Box>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(2, 1fr)' },
              gap: '0.8rem',
            }}
          >
            {[
              ['Visits', passport.visitCount],
              ['Favorites', passport.favoriteCount],
              ['Tracks found', passport.trackCount],
              ['Releases', passport.releaseCount],
              ['First era', passport.firstYear || '-'],
              ['Latest era', passport.latestYear || '-'],
              ['Mood', passport.topMood],
              ['Storage', getFanStorageKey(artistName).split(':').pop()],
            ].map(([label, value]) => (
              <Box key={String(label)} sx={{ p: { xs: '10px', md: '1rem' }, ...surfaceSx }}>
                <Typography
                  sx={{
                    ...helperLabelSx,
                    color: 'var(--logoColor2)',
                    fontSize: { xs: '11px', md: '0.75rem' },
                    textTransform: 'none',
                  }}
                >
                  {label}
                </Typography>
                <Typography
                  sx={{
                    color: 'var(--logoColor)',
                    fontFamily: 'Hanson',
                    fontSize: { xs: '18px', md: '1rem' },
                    lineHeight: 1.1,
                    mt: '0.35rem',
                    overflowWrap: 'anywhere',
                  }}
                >
                  {value}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
      )}
    </Box>
  );

  const fanHubNavigation = (
    <BottomNavigation
      value={activeTab}
      onChange={(_, value) => openFeature(value)}
      showLabels
      sx={{
        backgroundColor: 'transparent',
        display: 'flex',
        gap: { xs: '3px', md: '0.35rem' },
        height: { xs: '62px', md: '4.1rem' },
        px: { xs: '0.25rem', md: '0.5rem' },
        boxSizing: 'border-box',
        transform: {
          xs: `perspective(900px) rotateX(${navTilt.y}deg) rotateY(${navTilt.x}deg)`,
          md: 'none',
        },
        transformOrigin: 'center bottom',
        transition: 'transform 0.18s ease-out',
        '.MuiBottomNavigationAction-root': {
          color: 'var(--logoColor)',
          opacity: 0.62,
          flex: '1 1 0',
          minWidth: 0,
          maxWidth: 'none',
          fontFamily: 'Poppins',
          gap: '6px',
          height: { xs: '50px', md: '2.95rem' },
          alignSelf: 'center',
          mx: 0,
          padding: { xs: '5px 3px 8px', md: '0.35rem' },
          position: 'relative',
          border: '1px solid transparent',
          borderRadius: { xs: '12px', md: '999px' },
          transition:
            'transform 0.18s ease, opacity 0.18s ease, color 0.18s ease, background-color 0.18s ease, border-color 0.18s ease',
          '&::before': {
            content: '""',
            position: 'absolute',
            left: '50%',
            top: '50%',
            width: { xs: 'calc(100% - 4px)', md: 'calc(100% - 0.2rem)' },
            height: { xs: '46px', md: '2.75rem' },
            borderRadius: { xs: '12px', md: '999px' },
            backgroundColor: 'color-mix(in srgb, var(--logoColor2) 16%, transparent)',
            border: '1px solid color-mix(in srgb, var(--logoColor2) 16%, transparent)',
            opacity: 0,
            transform: 'translate(-50%, -50%)',
            transition: 'opacity 0.18s ease',
          },
          '&.Mui-selected': {
            color: 'var(--logoColor2)',
            opacity: 1,
            transform: { xs: 'translateY(-1px)', md: 'none' },
            '&::before': {
              opacity: 1,
            },
          },
          '& svg': {
            width: { xs: '20px', md: '1.05rem' },
            height: { xs: '20px', md: '1.05rem' },
            position: 'relative',
            zIndex: 1,
          },
        },
        '.MuiBottomNavigationAction-label': {
          fontFamily: 'Poppins',
          fontWeight: 800,
          fontSize: { xs: '11.5px !important', md: '0.72rem !important' },
          lineHeight: 1,
          position: 'relative',
          zIndex: 1,
        },
      }}
    >
      <BottomNavigationAction
        label="Today"
        value="today"
        icon={<Sparkles style={bottomNavIconStyle} />}
      />
      <BottomNavigationAction
        label="Moods"
        value="moods"
        icon={<SlidersHorizontal style={bottomNavIconStyle} />}
      />
      <BottomNavigationAction
        label="Timeline"
        value="timeline"
        icon={<Album style={bottomNavIconStyle} />}
      />
      <BottomNavigationAction
        label="Passport"
        value="passport"
        icon={<Disc3 style={bottomNavIconStyle} />}
      />
    </BottomNavigation>
  );

  return (
    <>
      <GlobalStyles
        styles={{
          'body footer': {
            paddingBottom: 'calc(62px + 8px + 1.25rem + env(safe-area-inset-bottom, 0px)) !important',
          },
          '@media screen and (min-width: 900px)': {
            'body footer': {
              paddingBottom: 'calc(4.1rem + 1.25rem + 1.25rem + env(safe-area-inset-bottom, 0px)) !important',
            },
          },
        }}
      />
      {(!reallyIsMobile || !open) && (
        <Box
          sx={{
            position: 'fixed',
            left: { xs: '0.5rem', md: '50%' },
            right: { xs: '0.5rem', md: 'auto' },
            bottom: { xs: '0.5rem', md: '1.25rem' },
            transform: { xs: 'none', md: 'translateX(-50%)' },
            zIndex: 30,
            maxWidth: { xs: 'none', md: '37rem' },
            width: { xs: 'auto', md: '100%' },
            borderRadius: { xs: '14px', md: '3rem' },
            overflow: 'hidden',
            ...fanHubControlChrome,
          }}
        >
          {fanHubNavigation}
        </Box>
      )}

      <BaseModal
        isOpen={open}
        onClose={() => setOpen(false)}
        desktopModalMobileDrawer
        noPadding
        hideCloseButton
        drawerBottomOffset={fanHubDrawerBottomOffset}
        drawerMaxHeight={fanHubDrawerMaxHeight}
        footer={reallyIsMobile ? fanHubNavigation : undefined}
        bodySx={{
          ...panelSx,
          backgroundColor: { xs: 'var(--color5)', md: 'transparent' },
        }}
        footerSx={{
          borderTop: '1px solid color-mix(in srgb, var(--logoColor) 18%, transparent)',
          ...fanHubControlChrome,
        }}
      >
        {content}
      </BaseModal>
    </>
  );
}
